import React from 'react';
import { Typography } from 'antd';

const { Text } = Typography;

interface HtmlTextRendererProps {
  htmlContent: string;
  className?: string;
  style?: React.CSSProperties;
}

const HtmlTextRenderer: React.FC<HtmlTextRendererProps> = ({ 
  htmlContent, 
  className,
  style 
}) => {
  const parseHtmlToReact = (html: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    let currentIndex = 0;
    let key = 0;

    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^<>]*>/gi;
    const matches = Array.from(html.matchAll(tagRegex));

    matches.forEach((match, index) => {
      const beforeTag = html.slice(currentIndex, match.index);
      if (beforeTag) {
        nodes.push(...parseTextContent(beforeTag, key++));
      }

      const tag = match[1].toLowerCase();
      const isClosingTag = match[0].startsWith('</');
      
      if (!isClosingTag) {
        const nextClosingTag = matches.find((m, i) => 
          i > index && m[1].toLowerCase() === tag && m[0].startsWith('</')
        );
        
        if (nextClosingTag) {
          const startPos = match.index! + match[0].length;
          const endPos = nextClosingTag.index!;
          const innerContent = html.slice(startPos, endPos);
          
          nodes.push(renderStyledElement(tag, innerContent, key++));
          currentIndex = nextClosingTag.index! + nextClosingTag[0].length;
        }
      }
    });

    const remainingText = html.slice(currentIndex);
    if (remainingText) {
      nodes.push(...parseTextContent(remainingText, key++));
    }

    return nodes;
  };

  const parseTextContent = (text: string, baseKey: number): React.ReactNode[] => {
    const hashtagRegex = /(#[а-яА-Яa-zA-Z0-9_]+)/g;
    const parts = text.split(hashtagRegex);
    
    return parts.map((part, index) => {
      if (part.match(hashtagRegex)) {
        return (
          <Text 
            key={`${baseKey}-${index}`}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {part}
          </Text>
        );
      }
      return part || null;
    }).filter(Boolean);
  };

  const renderStyledElement = (tag: string, content: string, key: number): React.ReactNode => {
    const innerNodes = parseHtmlToReact(content);
    
    switch (tag) {
      case 'br':
        return <br key={key} />;
      case 'b':
      case 'strong':
        return <Text key={key} strong>{innerNodes}</Text>;
      case 'i':
      case 'em':
        return <Text key={key} italic>{innerNodes}</Text>;
      case 'u':
        return <Text key={key} underline>{innerNodes}</Text>;
      case 'code':
        return <Text key={key} code>{innerNodes}</Text>;
      case 'pre':
        return (
          <Text 
            key={key} 
            code 
            style={{ 
              display: 'block', 
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f5f5f5',
              padding: '8px',
              borderRadius: '4px',
              fontFamily: 'monospace'
            }}
          >
            {innerNodes}
          </Text>
        );
      case 'a':
        return (
          <Text 
            key={key}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {innerNodes}
          </Text>
        );
      case 'p':
        return (
          <span key={key} style={{ display: 'block' }}>
            {innerNodes}
          </span>
        );
      default:
        return <span key={key}>{innerNodes}</span>;
    }
  };

  const renderedContent = parseHtmlToReact(htmlContent);

  return (
    <span className={className} style={style}>
      {renderedContent}
    </span>
  );
};

export default HtmlTextRenderer;
